import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import AdminNavbar from "../components/AdminNavbar";
import AdminAPI from "../../services/adminApi";
import { toast } from "react-toastify";

function AdminPlanner() {
  const [tasks, setTasks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [farmers, setFarmers] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    due_date: ""
  });

  const [taskForm, setTaskForm] = useState({
    farmer_id: "",
    task_title: "",
    task_description: "",
    due_date: "",
    status: "pending"
  });

  useEffect(() => {
    fetchTasks();
    fetchAnalytics();
    fetchFarmers();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await AdminAPI.get(
        `/planner?search=${filters.search}&status=${filters.status}&due_date=${filters.due_date}`
      );
      setTasks(res.data);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load tasks");
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await AdminAPI.get(
        "/planner/analytics/summary"
      );
      setAnalytics(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchFarmers = async () => {
    try {
      const res = await AdminAPI.get("/farmers");
      setFarmers(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleTaskChange = (e) => {
    setTaskForm({
      ...taskForm,
      [e.target.name]: e.target.value
    });
  };

  const createTask = async (e) => {
    e.preventDefault();

    try {
      await AdminAPI.post("/planner", taskForm);

      toast.success("Task created");

      setTaskForm({
        farmer_id: "",
        task_title: "",
        task_description: "",
        due_date: "",
        status: "pending"
      });

      fetchTasks();
      fetchAnalytics();

    } catch (err) {
      toast.error("Task creation failed");
    }
  };

  const openTask = async (id) => {
    try {
      const res = await AdminAPI.get(`/planner/${id}`);

      setSelectedTask(res.data);

      setTaskForm({
        farmer_id: res.data.farmer_id,
        task_title: res.data.task_title,
        task_description: res.data.task_description,
        due_date: res.data.due_date
          ? res.data.due_date.split("T")[0]
          : "",
        status: res.data.status
      });

    } catch {
      toast.error("Failed to load task");
    }
  };

  const updateTask = async () => {
    try {
      await AdminAPI.put(
        `/planner/${selectedTask.id}`,
        taskForm
      );

      toast.success("Task updated");

      setSelectedTask(null);

      fetchTasks();
      fetchAnalytics();

    } catch {
      toast.error("Update failed");
    }
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Delete task?")) return;

    try {
      await AdminAPI.delete(`/planner/${id}`);

      toast.success("Task deleted");

      fetchTasks();
      fetchAnalytics();

    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="d-flex">
      <AdminSidebar />

      <div
        style={{
          flex: 1,
          background: "#f5f7fa",
          minHeight: "100vh",
          padding: "20px"
        }}
      >
        <AdminNavbar />

        <h2 className="mt-3">
          📅 Planner Management
        </h2>

        {/* Analytics */}
        {analytics && (
          <div className="row mt-4 g-3">
            <div className="col-md-3">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Total Tasks</h6>
                  <h3>{analytics.total_tasks}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Pending</h6>
                  <h3>{analytics.pending_tasks}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Completed</h6>
                  <h3>{analytics.completed_tasks}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Overdue</h6>
                  <h3>{analytics.overdue_tasks}</h3>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Task */}
        <div className="card shadow border-0 mt-4">
          <div className="card-body">
            <h4>Create Task</h4>

            <form
              onSubmit={createTask}
              className="row g-3 mt-2"
            >
              <div className="col-md-3">
                <select
                  name="farmer_id"
                  className="form-select"
                  value={taskForm.farmer_id}
                  onChange={handleTaskChange}
                  required
                >
                  <option value="">
                    Select Farmer
                  </option>

                  {farmers.map((farmer) => (
                    <option
                      key={farmer.id}
                      value={farmer.id}
                    >
                      {farmer.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <input
                  type="text"
                  name="task_title"
                  className="form-control"
                  placeholder="Task title"
                  value={taskForm.task_title}
                  onChange={handleTaskChange}
                  required
                />
              </div>

              <div className="col-md-3">
                <input
                  type="date"
                  name="due_date"
                  className="form-control"
                  value={taskForm.due_date}
                  onChange={handleTaskChange}
                />
              </div>

              <div className="col-md-3">
                <select
                  name="status"
                  className="form-select"
                  value={taskForm.status}
                  onChange={handleTaskChange}
                >
                  <option value="pending">
                    Pending
                  </option>
                  <option value="completed">
                    Completed
                  </option>
                </select>
              </div>

              <div className="col-12">
                <textarea
                  name="task_description"
                  className="form-control"
                  rows="3"
                  placeholder="Task description"
                  value={taskForm.task_description}
                  onChange={handleTaskChange}
                />
              </div>

              <div className="col-12">
                <button className="btn btn-success">
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Filters */}
        <div className="card shadow border-0 mt-4">
          <div className="card-body">
            <div className="d-flex gap-3 flex-wrap">
              <input
                type="text"
                name="search"
                className="form-control"
                style={{ width: "250px" }}
                placeholder="Search farmer/task"
                value={filters.search}
                onChange={handleFilterChange}
              />

              <select
                name="status"
                className="form-select"
                style={{ width: "180px" }}
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                <option value="pending">
                  Pending
                </option>
                <option value="completed">
                  Completed
                </option>
              </select>

              <input
                type="date"
                name="due_date"
                className="form-control"
                style={{ width: "220px" }}
                value={filters.due_date}
                onChange={handleFilterChange}
              />

              <button
                className="btn btn-success"
                onClick={fetchTasks}
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card shadow border-0 mt-4">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-success">
                  <tr>
                    <th>Farmer</th>
                    <th>Task</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {tasks.map((task) => (
                    <tr key={task.id}>
                      <td>{task.full_name}</td>
                      <td>{task.task_title}</td>
                      <td>{task.due_date}</td>
                      <td>{task.status}</td>

                      <td>
                        <button
                          className="btn btn-primary btn-sm me-2"
                          onClick={() => openTask(task.id)}
                        >
                          Edit
                        </button>

                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() =>
                            deleteTask(task.id)
                          }
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        {selectedTask && (
          <div
            className="modal d-block"
            style={{
              background: "rgba(0,0,0,0.6)"
            }}
          >
            <div className="modal-dialog">
              <div className="modal-content">

                <div className="modal-header">
                  <h5>Edit Task</h5>

                  <button
                    className="btn-close"
                    onClick={() =>
                      setSelectedTask(null)
                    }
                  />
                </div>

                <div className="modal-body">
                  <input
                    name="task_title"
                    className="form-control mb-3"
                    value={taskForm.task_title}
                    onChange={handleTaskChange}
                  />

                  <textarea
                    name="task_description"
                    className="form-control mb-3"
                    rows="3"
                    value={taskForm.task_description}
                    onChange={handleTaskChange}
                  />

                  <input
                    type="date"
                    name="due_date"
                    className="form-control mb-3"
                    value={taskForm.due_date}
                    onChange={handleTaskChange}
                  />

                  <select
                    name="status"
                    className="form-select"
                    value={taskForm.status}
                    onChange={handleTaskChange}
                  >
                    <option value="pending">
                      Pending
                    </option>
                    <option value="completed">
                      Completed
                    </option>
                  </select>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() =>
                      setSelectedTask(null)
                    }
                  >
                    Close
                  </button>

                  <button
                    className="btn btn-success"
                    onClick={updateTask}
                  >
                    Save Changes
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default AdminPlanner;